import React, { useContext, useState } from "react";
import { Badge, Text, Link, ResourceItem, InlineStack, BlockStack } from "@shopify/polaris";
import { BillFilledIcon } from "@shopify/polaris-icons";

// import helpers
import { baseHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext } from "lib/context";

// import props
import Popover from "lib/components/popover/popover";
import { rowProp } from "app/payments/modules/generic/list/propTypes";

// import cms and subFeature
import InvoiceModal from "app/payments/modules/generic/list/modal/invoice";
import actionData from "app/payments/modules/generic/list/cms/payment";
import { useNavigate } from "react-router-dom";

const topMargin10px = {
  marginTop: "10px",
};

function PaymentRow(props) {
  const { cms, currentUser } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { activePopover, item, isInvoiceEnabled = true, popoverDisable, setActivePopover, setBanner } = props;
  const { _id: id, orderId, isRefunded: isLineItemRefunded } = item;
  const { mainLink } = actionData(cms);
  const isVendor = baseHelper.isVendor(currentUser);
  const { CLOSED, COLLECT, PAID_AND_AVAILED, PAY_AND_COLLECT, REJECTED, RESERVE, SERVICE, SHIPPED } = constant;
  const [invoiceModalActive, setInvoiceModalActive] = useState(false);
  const renderOrderRow = (items) => {
    const {
      _id,
      accountingStatus,
      actualQuantity,
      commissionSeller,
      commissionTax,
      commissionVendor,
      createdAt,
      dueDate,
      fulfillmentType,
      isAutoExpiry,
      isAutoReject,
      isOrderBasedCommission,
      isRefund,
      moneyFormat,
      orderNumber,
      paidVia,
      price,
      priceTaxRate,
      rejectedByVendor = false,
      quantity,
      vendorId,
      shippingAmount,
      shippingTax,
      shopifyOrderDate,
      vendor,
    } = items;

    let { status } = items;

    let taxRate = 0;
    let totalOrderPayout = 0;
    let totalShippingAmount = 0;

    if (!priceTaxRate) {
      taxRate = parseFloat(commissionSeller || 0) + parseFloat(commissionTax || 0);
    }

    if (priceTaxRate && !commissionTax) {
      taxRate = parseFloat(commissionSeller || 0);
    }

    if (priceTaxRate && commissionTax) {
      taxRate = parseFloat(commissionSeller || 0) + parseFloat(commissionTax || 0);
    }

    totalShippingAmount = parseFloat(shippingAmount || 0) + parseFloat(shippingTax || 0);

    totalOrderPayout = parseFloat((!rejectedByVendor && commissionVendor) || 0);

    const getItemCommissionTax = () => {
      const tax = baseHelper.getPrice(parseFloat(priceTaxRate || 0) / 100);
      const totalPriceInclusiveTax = baseHelper.getPrice(price * quantity);
      const priceExclusiveTax = baseHelper.getPrice(totalPriceInclusiveTax / (1 + parseFloat(tax)));
      const totalTax = parseFloat(totalPriceInclusiveTax) - parseFloat(priceExclusiveTax);
      const sellerCommissionInPercent = (commissionSeller / parseFloat(totalPriceInclusiveTax)) * 100;
      const sellerTaxOnCommission = totalTax * (sellerCommissionInPercent / 100);
      return sellerTaxOnCommission;
    };

    const isReserve = [SHIPPED, CLOSED].includes(status) && fulfillmentType === RESERVE;
    const isCollect = [SHIPPED, CLOSED].includes(status) && fulfillmentType === COLLECT;
    const isService = [SHIPPED, CLOSED].includes(status) && fulfillmentType === SERVICE;
    const isRefunded =
      (isRefund && fulfillmentType === COLLECT) || (fulfillmentType === RESERVE && actualQuantity === 0);

    const isAcceptedByOperator = rejectedByVendor && status === constant.ACCEPTED;
    const isFulfilledByOperator = rejectedByVendor && status === constant.SHIPPED;

    if (isReserve || isCollect || isService) {
      status = isService ? PAID_AND_AVAILED : PAY_AND_COLLECT;
    }

    if (isRefunded || rejectedByVendor) {
      status = REJECTED;
    }

    if (isService) {
      status = PAID_AND_AVAILED;
    }

    if (isRefunded) {
      status = REJECTED;
    }

    const autoStatus = isRefund && isAutoReject ? constant.AUTO_REJECT : isAutoExpiry && constant.NO_SHOW;
    status = autoStatus || status;

    const badgeStatus = baseHelper.ucFirst(status);
    const badgeType = baseHelper.getBadgeType(status);
    const accountingBadgeStatus = baseHelper.ucFirst(accountingStatus);
    const accountingBadgeType = baseHelper.getBadgeType(accountingStatus);

    return (
      <BlockStack>
        <BlockStack blockAlign="start">
          <InlineStack align="space-between">
            <BlockStack>
              <Text as="span">
                <Link onClick={() => navigate(`${mainLink.orderNumber}${orderId}`)}>{`#${orderNumber}`}</Link>
              </Text>
              <Text variant="bodySm" as="p">
                <Text as="span" tone="subdued">
                  {`${cms("label.creationDate")}: ${baseHelper.formatDate(createdAt || shopifyOrderDate, true)}`}
                </Text>
                <br />
                <div className="hidden" title={vendor}>
                  {`${cms("label.vendor")}: `}
                  {(isVendor && vendor) || <Link url={`/providers/view/${vendorId}`}>{vendor}</Link>}
                </div>
                <Text as="span">
                  {` ${cms("label.commission")}: ${moneyFormat} ${baseHelper.getPrice(commissionSeller)}`}
                </Text>
                <br />
                {priceTaxRate && !commissionTax && !isOrderBasedCommission ? (
                  <Text as="span">
                    {` ${cms("label.commissionTax")}: ${moneyFormat} ${baseHelper.getPrice(
                      getItemCommissionTax()
                    )} (Incl.)`}
                  </Text>
                ) : (
                  <Text as="span">
                    {` ${cms("label.commissionTax")}: ${moneyFormat} ${baseHelper.getPrice(commissionTax)}`}
                  </Text>
                )}
                <br />
                <Text as="span">
                  {` ${cms("label.vendorPayout")}: ${moneyFormat} ${baseHelper.getPrice(totalOrderPayout)}`}
                </Text>
                <br />
                {!!shippingAmount && (
                  <>
                    <Text as="span">
                      {`${cms("label.shipping")}: ${moneyFormat} ${baseHelper.getPrice(totalShippingAmount)}`}
                    </Text>
                    <br />
                  </>
                )}
                {!(isReserve || isCollect) && (
                  <>
                    <Text as="span">{`${cms("label.dueDate")}: ${baseHelper.formatDate(dueDate)}`}</Text>
                    <br />
                  </>
                )}
                {/* {!paidVia && (
                <Text as="span">{`${cms("label.manual")}: ${baseHelper.formatDate(createdAt, false)}`}</Text>
              )} */}
              </Text>
            </BlockStack>

            {isInvoiceEnabled && (
              <Popover
                // eslint-disable-next-line no-underscore-dangle
                active={activePopover[_id]}
                // eslint-disable-next-line no-underscore-dangle
                setActive={() =>
                  popoverDisable ? setActivePopover(false) : setActivePopover({ [_id]: !activePopover[_id] })
                }
                options={[
                  {
                    content: cms("label.invoice"),
                    icon: BillFilledIcon,
                    onAction: () => setInvoiceModalActive(true),
                    disabled: [REJECTED, constant.AUTO_REJECT, constant.NO_SHOW].includes(status) || rejectedByVendor,
                  },
                ]}
              />
            )}
          </InlineStack>
        </BlockStack>

        <div style={topMargin10px}>
          <InlineStack gap="400">
            <Badge tone={badgeType}>{badgeStatus}</Badge>
            {isAcceptedByOperator && (
              <Badge tone={baseHelper.getBadgeType("accepted by operator")}>Accepted By Operator</Badge>
            )}
            {isFulfilledByOperator && (
              <Badge tone={baseHelper.getBadgeType("fulfilled by operator")}>Fulfilled By Operator</Badge>
            )}
            <Badge tone={accountingBadgeType}>{accountingBadgeStatus}</Badge>
            {isLineItemRefunded && <Badge tone={constant.CRITICAL}>Refunded</Badge>}
          </InlineStack>
        </div>
      </BlockStack>
    );
  };

  return (
    <>
      <ResourceItem id={id}>{renderOrderRow(item)}</ResourceItem>
      {invoiceModalActive && (
        <InvoiceModal
          active={invoiceModalActive}
          handleClose={() => setInvoiceModalActive(!invoiceModalActive)}
          id={id}
          setActive={setInvoiceModalActive}
          item={item}
          setBanner={setBanner}
        />
      )}
    </>
  );
}

PaymentRow.propTypes = rowProp.type;
export default PaymentRow;
